"use client"

import { useEffect } from "react"

// How many ancestors to walk up from the tapped node looking for an editable
// target. Text usually sits at most a couple of wrappers below the tap point;
// bounding the walk keeps a tap on a big container from resolving a distant
// editable far up the tree.
const MAX_ANCESTOR_HOPS = 5

/**
 * Mobile tap-to-edit. The hover-pencil affordance (EditOverlay) doesn't exist on
 * touch, so on mobile a single tap picks the nearest editable element under the
 * finger. While `active`, a capture-phase `click` listener on document walks up
 * from the tapped node to the nearest element `canEdit` accepts, and — if found —
 * cancels the tap (so a tapped link/button doesn't navigate) and calls `onPick`.
 * Taps inside the sheet/editor UI (`[data-ahead-editor-ui]`) are ignored.
 * Renders nothing.
 */
export function MobileTapCapture({
  active,
  canEdit,
  onPick,
}: {
  active: boolean
  canEdit: (el: HTMLElement) => boolean
  onPick: (el: HTMLElement) => void
}) {
  useEffect(() => {
    if (!active) return
    function onClick(e: MouseEvent) {
      const start = e.target as HTMLElement | null
      if (!start) return
      // Tap landed on the sheet / editor chrome itself — let it behave normally.
      if (start.closest("[data-ahead-editor-ui]")) return
      let el: HTMLElement | null = start
      for (let hops = 0; el && hops < MAX_ANCESTOR_HOPS; hops++) {
        // Reached the editor UI without finding a page target — stop.
        if (el.closest("[data-ahead-editor-ui]")) return
        if (canEdit(el)) {
          e.preventDefault()
          e.stopPropagation()
          onPick(el)
          return
        }
        el = el.parentElement
      }
    }
    document.addEventListener("click", onClick, true)
    return () => document.removeEventListener("click", onClick, true)
  }, [active, canEdit, onPick])

  return null
}
